import { NSARouterMakerResult } from 'namirasoft-account-react';
import { EncodingOperation } from 'namirasoft-core';
import { EntryInputRow, EntryLineInputRow, EntryRow, UserFullRow } from 'namirasoft-password';
import { EncryptionOperation, NSBox, NSBoxString, NSBoxTextArea, NSBoxURL, NSButton, NSColumn, NSRepeater, NSRow } from 'namirasoft-site-react';
import { Component, createRef, RefObject } from 'react';
import { EntryTabPageView } from '../EntryTabPageView';
import { HashOperation } from '../HashOperation';
import { PasswordEntities } from '../PasswordEntities';
import { PasswordLayout } from '../PasswordLayout';
import { PasswordRouterProps } from '../PasswordRouterProps';
import { Storage } from '../Storage';
import EntryLine from '../components/EntryLine';

export interface EntryEditPageProps extends NSARouterMakerResult, PasswordRouterProps
{
    isEdit: boolean;
    user: UserFullRow;
}
class EntryEditPage extends Component<EntryEditPageProps>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxURL_URL = createRef<NSBoxURL>();
    NSRepeater_EntryLines = createRef<NSRepeater<EntryLine, EntryLineInputRow>>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    private addEntryLine?: () => void;

    private async createHash(entry_lines: EntryLineInputRow[], url: string)
    {
        let username_el = entry_lines.find(el => el.name.toLocaleLowerCase() === "username");
        let username = username_el?.value ?? "";
        if (username_el?.sensitive)
            username = EncodingOperation.Base64Decode(await EncryptionOperation.AES256GCMDecrypt(await Storage.getMasterKey(this.props.user.id), username));
        return HashOperation.hashEntry(String(username), String(url));
    }

    override render()
    {
        return (
            <PasswordLayout<EntryRow, EntryInputRow>
                {...this.props}
                action={{
                    description: "Save a new login by entering its name, website url, username, and password (typed or generated from a Template)."
                }}
                pages={{
                    entity: PasswordEntities.entry(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: async (): Promise<EntryInputRow> =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let url = this.NSBoxURL_URL.current?.getValue() ?? "";
                                        let entry_line = await this.NSRepeater_EntryLines.current?.getValueAsync() ?? [];
                                        let hash = await this.createHash(entry_line, url);
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? "";
                                        
                                        return {
                                            workspace_id: "",
                                            name,
                                            url,
                                            entry_line,
                                            hash,
                                            description,
                                            entry_category: [],
                                            entry_field: [],
                                            entry_tag: []
                                        }
                                    },
                                    setRow: (row) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxURL_URL.current?.setValue(row.url);
                                        this.NSRepeater_EntryLines.current?.setValue(row.entry_line);
                                        this.NSBoxTextArea_Description.current?.setValue(row.description);
                                    }
                                },
                            },
                            tab_page_view: EntryTabPageView.getTabPage(this.props),
                        },
                        onLoaded: () =>
                        {
                            if (!this.props.isEdit)
                            {
                                this.NSRepeater_EntryLines.current?.setValue([
                                    { name: "Username", sensitive: false, value: "" },
                                    { name: "Password", sensitive: true, value: "" },
                                ]);
                            }
                        },
                    },
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            title="Name"
                            required
                            info={{ link: "https://namirasoft.com/password/console-guide/entry/#field-name" }}
                        />
                    </NSRow>
                    <NSBoxURL
                        ref={this.NSBoxURL_URL}
                        title="URL"
                        required={false}
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/password/console-guide/entry/#field-url" }}
                    />
                    <NSRepeater<EntryLine, EntryLineInputRow>
                        ref={this.NSRepeater_EntryLines}
                        title=""
                        required
                        hideHeader
                        createItem={(gIndex: number, lIndex: number, ref: RefObject<EntryLine>, onChange: () => void, onDelete?: (lIndex: number) => void) => (
                            <EntryLine
                                {...this.props}
                                key={`EntryEditPage_NSRepeater_EntryLine_key_${gIndex}`}
                                ref={ref}
                                index={lIndex}
                                deletable
                                onChanged={onChange}
                                onDelete={() => onDelete?.(lIndex)}
                            />
                        )}
                        getValue={async (item: EntryLine, checkError: boolean) =>
                        {
                            return item.getEncryptedValue(checkError);
                        }}
                        setValue={(item: EntryLine, _, value: EntryLineInputRow | null) =>
                        {
                            if (value)
                                item.setEncryptedValue(value);
                        }}
                        isEmpty={(_, value: EntryLineInputRow | null) =>
                        {
                            return !EntryLine.IS_DIRTY(value);
                        }}
                        onExternalAddRef={(add) => this.addEntryLine = add}
                        style={{ width: "100%", maxWidth: NSBox.width.double }}
                    />
                    <NSButton
                        title="Add Entry Line"
                        onClick={{
                            showLoading: false,
                            action: () => this.addEntryLine?.(),
                        }}
                        icon={{ src: "https://static.namirasoft.com/image/concept/add/blue.svg", alt: "Add" }}
                        classList={["mx-auto"]}
                    />
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        title="Description"
                        required={false}
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/password/console-guide/entry/#field-description" }}
                    />
                </NSColumn>
            </PasswordLayout>
        )
    }
}

export default EntryEditPage;